import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header.jsx';
import OTPVerification from '../components/OTPVerification';
import logo from '../Images/logo rashi jyotish.png';
import api from '../services/api';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1); // 1: Form, 2: OTP Verification
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    full_name: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      // Send OTP to email
      const otpResponse = await api.post('/otp/send', {
        email: formData.email,
        purpose: 'registration'
      });

      if (otpResponse.data.success) {
        setSuccess('OTP sent to your email! Please verify to complete registration.');
        setStep(2); // Move to OTP verification step
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerified = async (otp) => {
    setLoading(true);
    setError('');

    try {
      // Register user with verified OTP
      const response = await register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        phone: formData.phone,
        otp: otp
      });

      if (response.data && response.data.token) {
        setSuccess('Registration successful! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
      setStep(1); // Go back to form
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div style={{
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        {step === 1 ? (
          // Step 1: Registration Form
          <div className="card" style={{ maxWidth: '500px', width: '100%', padding: '40px' }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <img src={logo} alt="SRJ Logo" style={{ width: '80px', marginBottom: '16px' }} />
              <h1 style={{ color: 'var(--maroon)', fontFamily: "'Playfair Display',serif", marginBottom: '8px' }}>
                Create Account
              </h1>
              <p style={{ color: 'var(--muted)' }}>Join our spiritual community</p>
            </div>

            {error && (
              <div style={{
                padding: '12px',
                background: 'rgba(220, 38, 38, 0.1)',
                border: '1px solid rgba(220, 38, 38, 0.3)',
                borderRadius: '8px',
                color: '#dc2626',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            {success && (
              <div style={{
                padding: '12px',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px',
                color: '#16a34a',
                marginBottom: '20px',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                ✓ {success}
              </div>
            )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="full_name" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Full Name *
            </label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              required
              placeholder="John Doe"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="username" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Username *
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              minLength="3"
              placeholder="johndoe"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="email" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="phone" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+91 1234567890"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Password *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
              placeholder="••••••••"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="confirm_password" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Confirm Password *
            </label>
            <input
              type="password"
              id="confirm_password"
              name="confirm_password"
              value={formData.confirm_password}
              onChange={handleChange}
              required
              placeholder="••••••••"
              style={{ width: '100%' }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: 'var(--accent-grad)',
              border: 'none',
              borderRadius: '10px',
              color: '#3b0b12',
              fontWeight: '800',
              fontSize: '16px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
            Already have an account?{' '}
            <Link to="/signin" style={{ color: 'var(--maroon)', fontWeight: '600', textDecoration: 'none' }}>
              Sign in here
            </Link>
          </p>
          <p style={{ color: 'var(--muted)', fontSize: '14px', marginTop: '12px' }}>
            <Link to="/dashboard" style={{ color: 'var(--maroon)', textDecoration: 'none' }}>
              ← Back to Home
            </Link>
          </p>
        </div>
      </div>
    ) : (
      // Step 2: OTP Verification
      <OTPVerification
        email={formData.email}
        purpose="registration"
        onVerified={handleOTPVerified}
        onCancel={() => setStep(1)}
        title="Verify Your Email"
        subtitle="Enter the 6-digit code sent to"
        skipVerification={true}
      />
    )}
    </div>
    </>
  );
};

export default Register;
